import { h } from 'preact';
import { useState, useEffect } from 'preact/hooks';
import { t, translateError } from '../i18n.js';
import { api } from '../state/auth.js';
import { loadTheme } from '../state/theme.js';
import Card from '../components/shared/Card.jsx';
import Button from '../components/shared/Button.jsx';
import FileUpload from '../components/shared/FileUpload.jsx';
import SubscriptionUpgradeModal from '../components/SubscriptionUpgradeModal.jsx';
import { useAuth } from '../hooks/useAuth.js';
import { MODULE_INFO } from '../utils/plan.js';
import AiSettingsCard from '../components/shared/AiSettingsCard.jsx';
import ClubExportCard from '../components/shared/ClubExportCard.jsx';
import ComplexLinkCard from '../components/shared/ComplexLinkCard.jsx';
import { VisibilityToggle } from './SetupView.jsx';
import { setup, loadSetup } from '../state/setup.js';
import { route } from 'preact-router';

const TIER_RANK = { basis: 0, plus: 1, professional: 2 };

const COLOR_FIELDS = [
  { key: 'primary_color',   cssVar: '--color-primary',   label: 'club.primaryColor' },
  { key: 'secondary_color', cssVar: '--color-secondary', label: 'club.secondaryColor' },
  { key: 'accent_color',    cssVar: '--color-accent',    label: 'club.accentColor' },
  { key: 'sidebar_color',   cssVar: '--sidebar-bg',      label: 'club.sidebarColor' }
];

export default function ClubSettingsView() {
  const [form, setForm] = useState({
    name: '',
    primary_color: '#16a34a',
    secondary_color: '#ffffff',
    accent_color: '#eab308',
    sidebar_color: '#0b0f14'
  });
  const [logoFile, setLogoFile] = useState(null);
  const [bannerFile, setBannerFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(true);
  const [subscription, setSubscription] = useState(null);
  const [upgradeModal, setUpgradeModal] = useState(false);
  const auth = useAuth();
  const canUpgrade = auth.hasPermission('settings', 'update');
  // A sportcomplex's subscription is arranged with 4U, not with club plans
  const isComplex = auth.isComplex;

  useEffect(() => {
    Promise.all([
      api('/api/clubs/current'),
      api('/api/clubs/subscription'),
    ]).then(([clubData, subData]) => {
      setForm({
        name: clubData.name || '',
        primary_color: clubData.colors?.primary || '#16a34a',
        secondary_color: clubData.colors?.secondary || '#ffffff',
        accent_color: clubData.colors?.accent || '#eab308',
        sidebar_color: clubData.colors?.sidebar || '#0b0f14'
      });
      setSubscription(subData);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  function handleColorChange(key, cssVar, value) {
    setForm(f => ({ ...f, [key]: value }));
    document.documentElement.style.setProperty(cssVar, value);
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setSaving(true);
    setError('');
    setSuccess(false);

    try {
      const formData = new FormData();
      formData.append('name', form.name);
      COLOR_FIELDS.forEach(({ key }) => formData.append(key, form[key]));
      if (logoFile) formData.append('logo', logoFile);
      if (bannerFile) formData.append('banner', bannerFile);

      const res = await fetch('/api/clubs/settings', { method: 'PUT', body: formData });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw { code: data.error || 'server_error' };
      }
      await loadTheme();
      setSuccess(true);
    } catch (err) {
      setError(translateError(err.code || 'server_error'));
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return <div class="text-center text-muted" style="padding:60px">{t('common.loading')}</div>;
  }

  return (
    <div>
      <div class="page-header">
        <h1 class="page-title">{t('club.settings')}</h1>
      </div>

      {error && <div class="alert alert-error mb-4">{error}</div>}
      {success && <div class="alert alert-success mb-4">{t('club.saved')}</div>}

      <form onSubmit={handleSubmit}>
        <Card title={t('club.name')} accent>
          <div class="form-group">
            <label class="form-label">{t('club.name')}</label>
            <input class="form-input" type="text" required value={form.name}
              onInput={e => setForm(f => ({ ...f, name: e.target.value }))} />
          </div>
        </Card>

        <Card title={t('club.primaryColor')} style="margin-top:16px">
          <div class="grid-2">
            {COLOR_FIELDS.map(({ key, cssVar, label }) => (
              <div key={key} class="form-group">
                <label class="form-label">{t(label)}</label>
                <div class="flex items-center gap-3">
                  <input class="form-color" type="color" value={form[key]}
                    onInput={e => handleColorChange(key, cssVar, e.target.value)} />
                  <span class="text-sm text-muted">{form[key]}</span>
                </div>
              </div>
            ))}
          </div>
        </Card>

        <Card title={t('club.logo')} style="margin-top:16px">
          <div class="grid-2">
            <div class="form-group">
              <label class="form-label">{t('club.logo')}</label>
              <FileUpload label={t('club.logo')} accept="image/*" onChange={setLogoFile} />
              {logoFile && <span class="text-sm text-muted">{logoFile.name}</span>}
            </div>
            <div class="form-group">
              <label class="form-label">{t('club.banner')}</label>
              <FileUpload label={t('club.banner')} accept="image/*" onChange={setBannerFile} />
              {bannerFile && <span class="text-sm text-muted">{bannerFile.name}</span>}
            </div>
          </div>
        </Card>

        <div class="mt-4">
          <Button type="submit" disabled={saving}>
            {saving ? t('common.loading') : t('club.save')}
          </Button>
        </div>
      </form>

      {subscription && !isComplex && (
        <Card title="Abonnement" style="margin-top:24px">
          <div style="display:flex;align-items:flex-start;justify-content:space-between;gap:16px;flex-wrap:wrap">
            <div>
              <div style="display:flex;align-items:center;gap:10px;margin-bottom:8px">
                <span style="font-size:18px;font-weight:700;color:var(--color-text)">{subscription.tierLabel}</span>
                <span style="font-size:12px;font-weight:600;background:var(--color-bg-secondary);color:var(--color-text-secondary);padding:3px 10px;border-radius:10px;border:1px solid var(--color-border)">
                  €{subscription.departments?.totalMonthly ?? subscription.priceMonthly}/maand
                </span>
              </div>
              <div style="font-size:14px;color:var(--color-text-secondary)">
                {subscription.currentUsers} van de {subscription.maxUsers ?? '∞'} gebruikers actief
              </div>
              {subscription.departments?.extra > 0 && (
                <div style="font-size:14px;color:var(--color-text-secondary);margin-top:4px">
                  {subscription.departments.count} afdelingen: €{subscription.priceMonthly} + {subscription.departments.extra} × €{subscription.departments.surchargeMonthly}
                </div>
              )}
              {subscription.maxUsers && (
                <div style="margin-top:10px;height:6px;background:var(--color-bg-secondary);border-radius:3px;overflow:hidden;max-width:240px">
                  <div style={{
                    height: '100%',
                    borderRadius: '3px',
                    background: subscription.currentUsers / subscription.maxUsers > 0.8
                      ? 'var(--color-warning, #f59e0b)'
                      : 'var(--color-primary)',
                    width: `${Math.min(100, (subscription.currentUsers / subscription.maxUsers) * 100)}%`
                  }} />
                </div>
              )}
            </div>
            {subscription.nextTier && canUpgrade && (
              <Button onClick={() => setUpgradeModal(true)}>
                Upgraden naar {subscription.nextTier.label}
              </Button>
            )}
          </div>
          {subscription.scheduled && <PlannedChange scheduled={subscription.scheduled} />}
          <PlanModules subscription={subscription} />
        </Card>
      )}

      {canUpgrade && !isComplex && !auth.isPlatformAdmin && <SetupRouteCard />}

      {canUpgrade && !isComplex && <ComplexLinkCard />}

      {canUpgrade && !isComplex && <AiSettingsCard />}

      {canUpgrade && <ClubExportCard />}

      {upgradeModal && subscription?.nextTier && (
        <SubscriptionUpgradeModal
          isOpen={upgradeModal}
          currentTierLabel={subscription.tierLabel}
          nextTier={subscription.nextTier}
          reason="choice"
          onClose={() => setUpgradeModal(false)}
        />
      )}
    </div>
  );
}

// Which modules the plan opens, and what the bigger plans add
const BASE_MODULES = { icon: '📋', title: 'Taken, processen, kalender, documenten, jaarplanning en nieuws' };
// A smaller plan or the end of the subscription that 4U planned: from when, and what it means
function PlannedChange({ scheduled }) {
  const date = new Date(`${scheduled.effectiveOn}T00:00:00Z`).toLocaleDateString('nl-NL', { day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC' });
  if (scheduled.kind === 'cancel') {
    return (
      <div style="margin-top:16px;border:1px solid #fca5a5;background:#fef2f2;border-radius:8px;padding:12px 14px;font-size:14px;color:#7f1d1d">
        <div style="font-weight:600;margin-bottom:4px">Vanaf {date} is jullie abonnement beëindigd</div>
        <div>Tot die dag werkt alles zoals nu. Daarna kunnen leden niet meer bij de club in de app, gaan openbare pagina's zoals het klussenbord offline en gaan er geen meldingen of mails meer uit. Alle gegevens blijven bewaard.</div>
        <div style="margin-top:6px">Vragen hierover? Mail naar support@vereniging4u.nl.</div>
      </div>
    );
  }
  return (
    <div style="margin-top:16px;border:1px solid #fcd34d;background:#fffbeb;border-radius:8px;padding:12px 14px;font-size:14px;color:#78350f">
      <div style="font-weight:600;margin-bottom:4px">Vanaf {date} is jullie pakket {scheduled.toLabel}</div>
      {scheduled.lost?.length > 0 && <div>Dan gaan op slot: {scheduled.lost.join(', ')}. Wat erin staat blijft bewaard.</div>}
      {scheduled.overLimit && <div>Jullie hebben {scheduled.overLimit.members} actieve leden; {scheduled.toLabel} staat er {scheduled.overLimit.max} toe. Iedereen blijft lid, maar nieuwe leden toevoegen kan pas weer onder de {scheduled.overLimit.max}.</div>}
      {scheduled.aiKey && <div>De koppeling met jullie AI-account wordt dan verwijderd.</div>}
      <div style="margin-top:6px">Vragen hierover? Mail naar support@vereniging4u.nl.</div>
    </div>
  );
}

function PlanModules({ subscription }) {
  const open = [BASE_MODULES, ...(subscription.modules || []).map(m => MODULE_INFO[m]).filter(Boolean)];
  const locked = subscription.locked || [];
  const byTier = locked.reduce((acc, l) => {
    // Storingen at a sportcomplex: the complex's places are open, the club's own are not
    const info = l.complex && MODULE_INFO[l.module] ? { ...MODULE_INFO[l.module], title: `${MODULE_INFO[l.module].title} op eigen plekken (op ${l.complex} al open)` } : MODULE_INFO[l.module];
    (acc[l.tier] ||= { label: l.tierLabel, price: l.priceMonthly, rank: TIER_RANK[l.tier], items: [] }).items.push(info);
    return acc;
  }, {});
  const list = items => (
    <ul style="margin:6px 0 0;padding-left:18px;font-size:13px;line-height:1.7;color:var(--color-text-secondary)">
      {items.filter(Boolean).map(m => <li key={m.title}>{m.icon} {m.title}</li>)}
    </ul>
  );
  return (
    <div style="display:grid;grid-template-columns:repeat(auto-fit,minmax(220px,1fr));gap:16px;margin-top:18px;padding-top:16px;border-top:1px solid var(--color-border)">
      <div>
        <div style="font-size:12px;font-weight:600;text-transform:uppercase;letter-spacing:.04em;color:var(--color-text-muted)">In je pakket</div>
        {list(open)}
      </div>
      {Object.values(byTier).sort((a, b) => a.rank - b.rank).map(t => (
        <div key={t.label}>
          <div style="font-size:12px;font-weight:600;text-transform:uppercase;letter-spacing:.04em;color:var(--color-text-muted)">🔒 Met {t.label} (€{t.price}/maand){t.rank === 2 && byTier.plus ? ', bovenop Plus' : ''}</div>
          {list(t.items)}
        </div>
      ))}
    </div>
  );
}

// De inrichtingsroute: openen, en weer in menu en dashboard zetten als hij verborgen is
function SetupRouteCard() {
  useEffect(() => { loadSetup({ force: true }); }, []);
  const s = setup.value;
  if (!s || s.unavailable) return null;
  return (
    <Card title="Inrichtingsroute" style="margin-top:16px">
      <div class="text-sm text-muted" style="margin-bottom:12px">
        Stap voor stap de club inrichten, met uitleg en voorbeelden. {s.done} van {s.total} stappen klaar{s.hidden ? '; staat nu niet in het menu en op het dashboard' : ''}.
      </div>
      <div style="display:flex;gap:10px;flex-wrap:wrap;align-items:center">
        <Button variant="secondary" onClick={() => route('/inrichten')}>Open de inrichting</Button>
        <VisibilityToggle hidden={s.hidden} />
      </div>
    </Card>
  );
}
